import React, { useState } from 'react';
import { i18nFn, RewardsOffersDTO } from '@springbig/shared';
import { VStack, HStack, Button, Box } from 'native-base';
import { CardTitle } from '~/pages/homePage/components/CardTitle';
import { Caption } from '~/shared/Typography';
import { useHomePageStyles } from '~/shared/styles';
import { TabSelector } from '~/pages/homePage/components/TabSelector';
import { format, isAfter } from 'date-fns';
import { useAppNavigate } from '~/pages';

type RewardsOffersProps = {
    data: RewardsOffersDTO;
    t: i18nFn;
};

export const RewardsOffers = ({
    data,
    t,
}: RewardsOffersProps) => {

    const { navigate, params } = useAppNavigate();
    const [category, setCategory] = useState<'total' | 'last_week'>('total');

    const tabs = [
        {
            name: t('home_page.filters.total'),
            action: () => setCategory('total'),
            isSelected: category === 'total',
        },
        {
            name: t('home_page.filters.week'),
            action: () => setCategory('last_week'),
            isSelected: category === 'last_week',
        },
    ];

    const {
        button_background,
        button_text,
        primary_color,
        pressable_text,
        sub_text,
    } = useHomePageStyles();

    return (
        <Box>
            <HStack justifyContent="space-evenly" alignItems="center" mb={2}>
                { data.highest_redeemable_reward &&
                    <CardTitle
                        title={t('home_page.rewards.title')}
                        icon={'faAward'}
                    />
                }
                { data.latest_offer &&
                    <CardTitle
                        title={t('home_page.offers.title')}
                        icon={'faGift'}
                    />
                }
            </HStack>
            <HStack space={4} justifyContent={'space-between'}>
                { data.highest_redeemable_reward &&
                    <VStack flex={1} space={2}>
                        <VStack
                            justifyContent={'space-between'}
                            alignItems={'center'}
                            bg={primary_color}
                            rounded={'lg'}
                            p={2}
                            space={2}
                            flex={1}
                        >
                            <Caption fontWeight={'500'}>
                                { t('home_page.rewards.highest') }
                            </Caption>
                            <VStack alignItems={'center'}>
                                <Caption color={pressable_text}>
                                    { data.highest_redeemable_reward?.item_name || '-' }
                                </Caption>
                                { data.highest_redeemable_reward &&
                                    <Caption fontSize={12} color={sub_text}>
                                        { t('home_page.rewards.value', [data.highest_redeemable_reward?.value]) }
                                    </Caption>
                                }
                            </VStack>
                            { data.highest_redeemable_reward ?
                                <Button
                                    bg={button_background}
                                    py={1}
                                    px={4}
                                    rounded={'full'}
                                    _hover={{ bg: button_background }}
                                    onPress={() => navigate(`w/${params.wallet_id}/rewards/${data.highest_redeemable_reward.id}`)}
                                >
                                    <Caption color={button_text}>
                                        { t('home_page.rewards.get') }
                                    </Caption>
                                </Button>
                                :
                                <Box></Box> // empty box insures flex behaves expectedly
                            }
                        </VStack>
                        <HStack space={2}>
                            <VStack
                                alignItems={'center'}
                                bg={primary_color}
                                rounded={'lg'}
                                p={2}
                                space={2}
                                flex={1}
                                borderWidth={1}
                                borderColor={category === 'total' ? primary_color : pressable_text}
                            >
                                <Caption fontWeight={'500'}>
                                    { t('home_page.rewards.earned') }
                                </Caption>
                                <Caption color={pressable_text}>
                                    { data[category].rewards_earned }
                                </Caption>
                            </VStack>
                            <VStack
                                alignItems={'center'}
                                bg={primary_color}
                                rounded={'lg'}
                                p={2}
                                space={2}
                                flex={1}
                                borderWidth={1}
                                borderColor={category === 'total' ? primary_color : pressable_text}
                            >
                                <Caption fontWeight={'500'}>
                                    { t('home_page.rewards.redeemed') }
                                </Caption>
                                <Caption color={pressable_text}>
                                    { data[category].rewards_redeemed }
                                </Caption>
                            </VStack>
                        </HStack>
                    </VStack>
                }

                { data.latest_offer &&
                    <VStack flex={1} space={2}>
                        <VStack
                            justifyContent={'space-between'}
                            alignItems={'center'}
                            bg={primary_color}
                            rounded={'lg'}
                            p={2}
                            space={2}
                            flex={1}
                        >
                            <Caption fontWeight={'500'}>
                                { t('home_page.offers.latest') }
                            </Caption>
                            <Caption color={pressable_text} textAlign={'center'}>
                                { data.latest_offer?.item_name || '-' }
                            </Caption>
                            { data.latest_offer ?
                                <HStack space={1} justifyContent={'space-around'} alignItems={'center'}>
                                    {
                                        data.latest_offer.expiration_date &&
                                      isAfter(new Date(data.latest_offer.expiration_date), new Date()) &&
                                      <Caption fontSize={12} textAlign={'center'} color={sub_text}>
                                          {
                                              t(
                                                  'home_page.offers.expiration',
                                                  [format(new Date(data.latest_offer.expiration_date), 'MM/dd/yy')]
                                              )
                                          }
                                      </Caption>
                                    }
                                    <Button
                                        bg={button_background}
                                        py={1}
                                        px={4}
                                        rounded={'full'}
                                        _hover={{ bg: button_background }}
                                        onPress={() => navigate(`w/${params.wallet_id}/offers/${data.latest_offer.id}`)}
                                    >
                                        <Caption color={button_text}>
                                            { t('home_page.offers.get') }
                                        </Caption>
                                    </Button>
                                    <Caption fontSize={12} textAlign={'center'} color={sub_text}>
                                        {
                                            t(
                                                'home_page.offers.redemptions',
                                                [data.latest_offer?.max_redemptions]
                                            )
                                        }
                                    </Caption>
                                </HStack>
                                :
                                <Box></Box> // empty box insures flex behaves expectedly
                            }
                        </VStack>
                        <HStack space={2}>
                            <VStack
                                alignItems={'center'}
                                bg={primary_color}
                                rounded={'lg'}
                                p={2}
                                space={2}
                                flex={1}
                                borderWidth={1}
                                borderColor={category === 'total' ? primary_color : pressable_text}
                            >
                                <Caption fontWeight={'500'}>
                                    { t('home_page.offers.earned') }
                                </Caption>
                                <Caption color={pressable_text}>
                                    { data[category].offers_earned }
                                </Caption>
                            </VStack>
                            <VStack
                                alignItems={'center'}
                                bg={primary_color}
                                rounded={'lg'}
                                p={2}
                                space={2}
                                flex={1}
                                borderWidth={1}
                                borderColor={category === 'total' ? primary_color : pressable_text}
                            >
                                <Caption fontWeight={'500'}>
                                    { t('home_page.offers.redeemed') }
                                </Caption>
                                <Caption color={pressable_text}>
                                    { data[category].offers_redeemed }
                                </Caption>
                            </VStack>
                        </HStack>
                    </VStack>
                }
            </HStack>
            <Box pt={4}>
                <TabSelector tabs={tabs} />
            </Box>
        </Box>
    );
};
